import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginAPI, verifyTokenAPI } from '../services/api';
import { useAppData } from './AppDataContext';
import mqttService from '../services/mqttService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const { updateAppData, clearAppData, permissionsReady } = useAppData();

  // Function to verify token validity
  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return false;
      }

      const response = await verifyTokenAPI();
      
      // If API call succeeds (200 status), token is valid
      if (response.success) {
        setTokenValid(true);
        
        // Extract frontend permissions from user object or response
        const frontendPerms = response.user?.frontendPermissions || 
                             response.frontendPermissions || 
                             [];
        
        // Update app data with the response
        updateAppData({
          metaData: response.metaData,
          user: response.user,
          roles: response.roles,
          permissions: response.permissions || [],
          frontendPermissions: frontendPerms
        });
        
        // Also update localStorage userData with latest user info including frontendPermissions
        if (response.user) {
          localStorage.setItem('userData', JSON.stringify(response.user));
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      setTokenValid(false);
      return false;
    }
  };

  // Function to clear authentication data
  const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setUser(null);
    setIsAuthenticated(false);
    setTokenValid(false);
    clearAppData(); // Also clear app data
    
    // Disconnect MQTT when logging out
    mqttService.disconnect();
  };

  useEffect(() => {
    // Check for existing session and verify token
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');
        
        if (token && userData) {
          const parsedUserData = JSON.parse(userData);
          
          // Verify token with API
          const isValid = await verifyToken();
          
          if (isValid) {
            setUser(parsedUserData);
            setIsAuthenticated(true);
            
            // Connect to MQTT after successful authentication
            try {
              await mqttService.connect();
            } catch (error) {
              console.error('[Auth] Failed to connect to MQTT:', error);
            }
          } else {
            // Token is invalid, clear auth data
            clearAuthData();
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear invalid data
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await loginAPI(username, password);
      
      if (response.success) {
        // Store token and user info
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userData', JSON.stringify(response.userInfo));
        
        // Update state in a single batch to prevent multiple re-renders
        setUser(response.userInfo);
        setIsAuthenticated(true);
        setTokenValid(true);
        
        // Update app data with the response
        updateAppData({
          metaData: response.metaData,
          user: response.userInfo,
          roles: response.roles,
          permissions: response.permissions || [],
          frontendPermissions: response.userInfo?.frontendPermissions || []
        });
        
        // Connect to MQTT after successful login
        try {
          await mqttService.connect();
        } catch (error) {
          console.error('[Auth] Failed to connect to MQTT:', error);
        }
        
        return { success: true, user: response.userInfo };
      } else {
        // Handle API error response
        const errorMessage = response.message || 'Login failed';
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      let errorMessage = 'An unexpected error occurred';
      
      if (error.message) {
        // Check if it's an HTTP error with a message
        if (error.message.includes('HTTP')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    clearAuthData();
  };

  // Function to manually verify token (can be called from components)
  const checkTokenValidity = async () => {
    const isValid = await verifyToken();
    if (!isValid) {
      clearAuthData();
    }
    return isValid;
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading,
    tokenValid,
    permissionsReady,
    checkTokenValidity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};